import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, where, getDocs, doc as docRef, getDoc, getFirestore } from 'firebase/firestore';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import app from '../../firebase/firebaseConfig';
import { formatDateTime } from '../../utils/dateFormatter';
import { useAuth } from '../../context/AuthContext';

const __app_id = app.options.appId;

// Helpers: normalize Firestore Timestamp / string / Date into milliseconds
const getTimestampMs = (value) => {
  if (!value) return 0;
  if (typeof value === 'object' && typeof value.toDate === 'function') {
    return value.toDate().getTime();
  }
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const t = Date.parse(value);
    if (!isNaN(t)) return t;
    const t2 = Date.parse(value + 'T00:00:00');
    return isNaN(t2) ? 0 : t2;
  }
  return 0;
};

// --- Pie Chart Label Rendering (like Herd Status) ---
const RADIAN = Math.PI / 180;
const renderCustomizedLabel = ({ cx, cy, midAngle, outerRadius, percent, name }) => {
    const radius = outerRadius + 25; // Position the label outside the pie
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    const sin = Math.sin(-midAngle * RADIAN);
    const cos = Math.cos(-midAngle * RADIAN);
    const sx = cx + (outerRadius + 10) * cos;
    const sy = cy + (outerRadius + 10) * sin;
    const mx = cx + (outerRadius + 20) * cos;
    const my = cy + (outerRadius + 20) * sin;
    const ex = mx + (cos >= 0 ? 1 : -1) * 12;
    const ey = my;
    const textAnchor = cos >= 0 ? 'start' : 'end';

    return (
        <g>
            <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke="#888" fill="none" />
            <circle cx={ex} cy={ey} r={2} fill="#888" stroke="none" />
            <text x={ex + (cos >= 0 ? 1 : -1) * 6} y={ey} textAnchor={textAnchor} fill="#333">{`${name} (${(percent * 100).toFixed(0)}%)`}</text>
        </g>
    );
};


const FeedDetailView = ({ feedItem, purchaseHistory = [], onBack, onRecordPurchaseClick }) => {
    const db = getFirestore(app);
    const { currentUser } = useAuth();
    const farmId = currentUser.farmId;

    const [consumptionHistory, setConsumptionHistory] = useState([]);
    const [isLoadingConsumption, setIsLoadingConsumption] = useState(true);

    useEffect(() => {
        const fetchConsumptionData = async () => {
            if (!farmId || !feedItem) return;

            setIsLoadingConsumption(true);
            try {
                const livestockPath = `artifacts/${__app_id}/farms/${farmId}/livestock`;
                const livestockQuery = query(collection(db, livestockPath));
                const livestockSnapshot = await getDocs(livestockQuery);
                const animals = livestockSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

                let allAllocations = [];

                const allocationPromises = animals.map(async (animal) => {
                    const allocationPath = `${livestockPath}/${animal.id}/feed_allocations`;
                    const allocationQuery = query(collection(db, allocationPath), where('feedItemId', '==', feedItem.id));
                    const allocationSnapshot = await getDocs(allocationQuery);
                    
                    const allocations = allocationSnapshot.docs.map(doc => ({
                        ...doc.data(),
                        id: doc.id,
                        cowName: animal.name || 'Unnamed'
                    }));
                    return allocations;
                });

                const results = await Promise.all(allocationPromises);
                allAllocations = results.flat();

                allAllocations.sort((a, b) => getTimestampMs(b.createdAt) - getTimestampMs(a.createdAt));
                setConsumptionHistory(allAllocations);

            } catch (err) {
                console.error('Error fetching consumption history:', err);
                setConsumptionHistory([]);
            } finally {
                setIsLoadingConsumption(false);
            }
        };

        fetchConsumptionData();

    }, [farmId, feedItem]);

    const summaryData = useMemo(() => {
        const totalPurchased = (purchaseHistory || []).reduce((sum, p) => sum + (p.quantity || 0), 0);
        const totalPurchaseCost = (purchaseHistory || []).reduce((sum, p) => sum + (p.totalCost || 0), 0);
        const totalConsumed = (consumptionHistory || []).reduce((sum, c) => sum + (c.quantity || 0), 0);
        const totalConsumedCost = (consumptionHistory || []).reduce((sum, c) => sum + (c.cost || 0), 0);
        const stockRemaining = feedItem?.stock || 0;
        const remainingValue = stockRemaining * (feedItem?.unitPrice || 0);

        return { totalPurchased, totalPurchaseCost, totalConsumed, totalConsumedCost, stockRemaining, remainingValue };
    }, [feedItem, purchaseHistory, consumptionHistory]);
    
    // --- Data processing for the Pie Chart ---
    const stockStatusData = useMemo(() => {
        return [
            { name: 'Consumed', value: parseFloat(summaryData.totalConsumed.toFixed(2)) },
            { name: 'Remaining', value: parseFloat(summaryData.stockRemaining.toFixed(2)) }
        ];
    }, [summaryData]);
    
    // --- Data processing for the Pie Chart ---
    const consumptionByAnimalData = useMemo(() => {
        const byAnimal = consumptionHistory.reduce((acc, curr) => {
            acc[curr.cowName] = (acc[curr.cowName] || 0) + curr.quantity;
            return acc;
        }, {});

        return Object.entries(byAnimal).map(([name, value]) => ({ name, value: parseFloat(value.toFixed(2)) }));
    }, [consumptionHistory]);

    const PIE_COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#AF19FF', '#FF19AF'];

    return (
        <div>
            <div className="flex justify-between items-center mb-4">
                <div>
                    <h2 className="text-3xl font-bold text-gray-800">{feedItem?.name}</h2>
                    <p className="text-sm text-gray-500">Detailed Inventory Report</p>
                </div>
                <div className="flex items-center space-x-3">
                    <button onClick={() => onRecordPurchaseClick(feedItem)} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700">+ Record Purchase</button>
                    <button onClick={onBack} className="text-sm text-indigo-600 hover:text-indigo-800">&larr; Back to Inventory</button>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white p-6 rounded-lg shadow">
                    <p className="text-sm font-medium text-gray-500">Total Purchased</p>
                    <p className="mt-1 text-3xl font-semibold text-gray-900">{summaryData.totalPurchased.toFixed(2)} kg</p>
                    <p className="text-xs text-gray-400">Total Cost: ₹{summaryData.totalPurchaseCost.toFixed(2)}</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow">
                    <p className="text-sm font-medium text-gray-500">Total Consumed</p>
                    <p className="mt-1 text-3xl font-semibold text-orange-600">{summaryData.totalConsumed.toFixed(2)} kg</p>
                    <p className="text-xs text-gray-400">Total Cost: ₹{summaryData.totalConsumedCost.toFixed(2)}</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow">
                    <p className="text-sm font-medium text-gray-500">Stock Remaining</p>
                    <p className="mt-1 text-3xl font-semibold text-blue-600">{summaryData.stockRemaining.toFixed(2)} kg</p>
                    <p className="text-xs text-gray-400">Estimated Value: ₹{summaryData.remainingValue.toFixed(2)}</p>
                </div>
            </div>

            {/* --- UPDATED: Charts Section --- */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                <div className="bg-white p-4 rounded-lg shadow">
                    <h3 className="text-lg font-semibold mb-4 text-gray-800">Stock Status</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <PieChart margin={{ top: 20, right: 30, left: 30, bottom: 20 }}>
                            <Pie data={stockStatusData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} fill="#8884d8" labelLine={false} label={renderCustomizedLabel}>
                                <Cell key={`cell-0`} fill={PIE_COLORS[3]} />
                                <Cell key={`cell-1`} fill={PIE_COLORS[1]} />
                            </Pie>
                            <Tooltip formatter={(value) => `${value} kg`} />
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
                <div className="bg-white p-4 rounded-lg shadow">
                    <h3 className="text-lg font-semibold mb-4 text-gray-800">Consumption by Heard</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <PieChart margin={{ top: 20, right: 30, left: 30, bottom: 20 }}>
                            <Pie data={consumptionByAnimalData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} fill="#8884d8" labelLine={false} label={renderCustomizedLabel}>
                                {consumptionByAnimalData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip formatter={(value) => `${value} kg`} />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* History Tables */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Purchase History */}
                <div>
                    <h3 className="text-lg font-semibold mb-2">Purchase History</h3>
                    <div className="overflow-x-auto bg-white rounded-lg shadow max-h-96 overflow-y-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50 sticky top-0">
                                <tr>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Quantity (kg)</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Price/kg (₹)</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Total Cost (₹)</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {(!purchaseHistory || purchaseHistory.length === 0) ? (
                                    <tr><td colSpan="4" className="text-center py-4">No purchase history.</td></tr>
                                ) : (
                                    purchaseHistory.map(p => (
                                        <tr key={p.id}>
                                            <td className="px-4 py-2">{formatDateTime(p.createdAt)}</td>
                                            <td className="px-4 py-2">{(p.quantity || 0).toFixed(2)}</td>
                                            <td className="px-4 py-2">₹{(p.unitPrice || 0).toFixed(2)}</td>
                                            <td className="px-4 py-2">₹{(p.totalCost || 0).toFixed(2)}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Consumption History (inline) */}
                <div>
                    <h3 className="text-lg font-semibold mb-2">Consumption History</h3>
                    <div className="overflow-x-auto bg-white rounded-lg shadow max-h-96 overflow-y-auto">
                        {isLoadingConsumption ? (
                            <p className="text-center py-4">Loading consumption history...</p>
                        ) : (
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50 sticky top-0">
                                    <tr>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Animal</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Quantity (kg)</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {consumptionHistory.length === 0 ? (
                                        <tr><td colSpan="3" className="text-center py-4">No consumption history found.</td></tr>
                                    ) : (
                                        consumptionHistory.map((c, i) => (
                                            <tr key={c.id || i}>
                                                <td className="px-4 py-2">{formatDateTime(c.createdAt)}</td>
                                                <td className="px-4 py-2 font-medium">{c.cowName}</td>
                                                <td className="px-4 py-2">{(c.quantity || 0).toFixed(2)}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FeedDetailView;
